import { useState } from 'react';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import SharedScriptixCaptionEditor from '@components/editors/shared-editor/shared-caption/shared-scriptix-caption-editor.tsx';
import cn from 'classnames';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

type Props = {
  segment: DocumentSubtitleSegmentV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  index: number;
  filteredItems: DocumentSubtitleSegmentV1[];
};

export const SharedCaptionSegment = ({
  index,
  segment,
  onWordOutOfView,
  onWordInView,
  filteredItems
}: Props) => {
  const { vars, cls } = useThemeVars();
  const { theme } = useThemeContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Theme-aware colors for hover/focus states
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const activeBorderColor = pick('rgb(191 219 254)', 'rgb(59 130 246)'); // blue-200 / blue-500
  const activeBgColor = pick('rgb(239 246 255)', 'rgba(30, 58, 138, 0.2)'); // blue-50 / blue-900/20
  const hoverBorderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const hoverBgColor = pick('rgb(249 250 251)', 'rgba(31, 41, 55, 0.3)'); // gray-50 / gray-800/30

  const getStyles = () => {
    if (isFocused) {
      return {
        borderColor: activeBorderColor,
        backgroundColor: activeBgColor
      };
    }
    if (isHovered) {
      return {
        borderColor: hoverBorderColor,
        backgroundColor: hoverBgColor
      };
    }
    // Default: use theme vars (secondLayer background, theme border)
    return {};
  };

  return (
    <div
      className={cn(
        'flex transition-all duration-200 border border-t-0',
        // Default state uses theme classes
        !isHovered && !isFocused && cls.border,
        !isHovered && !isFocused && 'bg-[var(--card-bg)]'
      )}
      style={{
        ...vars,
        ...getStyles(),
        // Reset font styles to prevent CSS inheritance from affecting segment content
        fontStyle: 'normal',
        fontWeight: 'normal',
        textDecoration: 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={() => setIsFocused(false)}
    >
      <div className="w-full">
        <SharedScriptixCaptionEditor
          index={index}
          segment={segment}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
          filteredItems={filteredItems}
        />
      </div>
    </div>
  );
};

export default SharedCaptionSegment;
